// src/components/ecommerce/ServiceCard.jsx
import React, { useState } from 'react';
import { 
  X,
  Plus,
  Minus,
  AlertCircle,
  Clock,
  Users,
  MessageCircle,
  Calendar,
  Star,
  Info,
  CheckCircle,
  SlidersHorizontal,
  MapPin,
  DollarSign,
  Zap,
  Award,
  ArrowRight,
  Loader2
} from 'lucide-react';

export const ServiceCard = ({ 
  service, 
  storeView = 'grid',
  onClick, 
  onBook,
  onContact,
  onViewBooking,
  isBooked = false, 
  bookingCount = 0,
  loading = false,
  styling = {},
  storeSettings = {},
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const hasOptions = service.options && Array.isArray(service.options) && service.options.length > 0;

  const calculateTotalPrice = () => {
    let total = parseFloat(service.price) || 0;
    
    if (!hasOptions) return total.toFixed(2);
    
    Object.entries(selectedOptions).forEach(([optionIndex, quantity]) => {
      const option = service.options[optionIndex];
      if (option && !option.isFree) {
        total += option.price * quantity;
      }
    });
    
    return total.toFixed(2);
  };

  // ✅ ADD: Handle option selection
  const handleOptionChange = (index, quantity) => {
    if (!hasOptions) return;
    
    const option = service.options[index];
    if (!option) return;
    
    if (quantity === 0) {
      const newOptions = { ...selectedOptions };
      delete newOptions[index];
      setSelectedOptions(newOptions);
    } else {
      const maxQty = option.maxQuantity || 999;
      const validQuantity = Math.min(quantity, maxQty);
      
      setSelectedOptions(prev => ({
        ...prev,
        [index]: validQuantity
      }));
    }
  };

  // ✅ ADD: Handle book with options
  const handleBookWithOptions = () => {
    const bookingData = {
      service,
      selectedOptions: hasOptions 
        ? service.options.map((option, index) => ({
            ...option,
            quantity: selectedOptions[index] || 0
          })).filter(opt => opt.quantity > 0)
        : []
    };
    
    onBook?.(bookingData);
    setShowOptionsModal(false);
  };

  // ✅ ADD: Check if there are required options not selected
  const hasUnselectedRequiredOptions = () => {
    if (!hasOptions) return false;
    
    return service.options.some((option, index) => 
      option.isRequired && !selectedOptions[index]
    );
  };

  // ✅ ADD: Handle button click based on booking status
  const handleBookClick = () => {
    console.log('🔍 Button clicked:', service.name);
    
    if (isBooked) {
      onViewBooking?.();
    } else if (hasOptions) {
      // Let parent handle options modal
      onClick?.(service);
    } else {
      // Direct booking
      onBook?.({ service, selectedOptions: [] });
    }
  };

  // Get button text based on status
  const getButtonText = () => {
    if (isBooked) {
      return bookingCount > 1 ? `Booked (${bookingCount})` : 'View Booking';
    }
    return hasOptions ? 'Order Now' : 'Book Now';
  };

  // ✅ ADD: Get button icon based on status
  const getButtonIcon = () => {
    if (isBooked) {
      return <CheckCircle className="h-4 w-4" />;
    }
    return <Calendar className="h-4 w-4" />;
  };

  const renderServiceFeatures = () => {
    if (!service.features || service.features.length === 0) return null;

    const displayFeatures = storeView === 'list' 
      ? service.features.slice(0, 5) 
      : service.features.slice(0, 3);

    return (
      <div className="space-y-1.5">
        {displayFeatures.map((feature, index) => (
          <div key={index} className="flex items-start gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">{feature}</span>
          </div>
        ))}
        {service.features.length > displayFeatures.length && (
          <div className="text-sm text-blue-600 font-medium">
            +{service.features.length - displayFeatures.length} more benefits
          </div>
        )}
      </div>
    );
  };

  if (storeView === 'list') {
    return (
      <div 
        className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow ${
          isBooked ? 'ring-2 ring-green-500 ring-opacity-50' : ''
        } ${className}`}
      >
        <div className="flex flex-col md:flex-row">
          {/* Service Image */}
          <div className="md:w-80 h-64 md:h-auto bg-gray-200 flex-shrink-0 relative overflow-hidden group">
            {service.images?.[0] && !imageError ? (
              <img
                src={service.images[0]}
                alt={service.name}
                onError={() => setImageError(true)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
                <Users className="h-20 w-20 text-blue-300" />
              </div>
            )}
            {service.featured && (
              <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1">
                <Star className="h-3 w-3" />
                Featured
              </div>
            )}
            {/* ✅ ADD: Booked Badge */}
            {isBooked && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                <CheckCircle className="h-3 w-3" />
                Booked
              </div>
            )}
          </div>

          {/* Service Details */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h3>
                {service.category && (
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    {service.category}
                  </span>
                )}
              </div>
              {service.price && (
                <div className="text-right ml-4">
                  <div className="text-3xl font-bold text-red-600">
                    R{service.price}
                  </div>
                  {service.duration && (
                    <div className="text-sm text-gray-500">per session</div>
                  )}
                </div>
              )}
            </div>

            <p className="text-gray-600 mb-4 line-clamp-3">{service.description}</p>

            {/* Service Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
              {service.duration && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>{service.duration}</span>
                </div>
              )}
              {service.deliveryMethod && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="capitalize">{service.deliveryMethod.replace('-', ' ')}</span>
                </div>
              )}
              {service.rating && (
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-medium">{service.rating}</span>
                  {service.reviewCount && (
                    <span className="text-gray-400">({service.reviewCount})</span>
                  )}
                </div>
              )}
            </div>

            {/* Service Features */}
            {renderServiceFeatures()}

            {/* Service Options Preview */}
            {hasOptions && !isBooked && (
              <div className="mb-4 pb-4 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  Available Options ({service.options.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {service.options.slice(0, 3).map((option, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                    >
                      {option.name}
                      {!option.isFree && <span className="font-semibold">+R{option.price}</span>}
                    </span>
                  ))}
                  {service.options.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{service.options.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookClick();
                }}
                disabled={loading}
                className="w-full py-2.5 px-4 rounded-lg font-semibold text-white"
                style={{ backgroundColor: styling.primaryColor || '#3b82f6' }}
              >
                {getButtonText()}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact?.();
                }}
                className="px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <MessageCircle className="h-5 w-5" />
                Inquire
              </button>
              <button
                onClick={onClick}
                className="px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Info className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <>
      <div 
        className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col ${
          isBooked ? 'ring-2 ring-green-500 ring-opacity-50' : ''
        } ${className}`}
      >
        {/* Service Image */}
        <div className="aspect-video bg-gray-200 relative overflow-hidden group cursor-pointer" onClick={onClick}>
          {service.images?.[0] && !imageError ? (
            <img
              src={service.images[0]}
              alt={service.name}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
              <Users className="h-16 w-16 text-blue-300" />
            </div>
          )}
          {service.featured && (
            <div className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <Star className="h-3 w-3" />
              Featured
            </div>
          )}
          {/* ✅ ADD: Booked Badge */}
          {isBooked && (
            <div className="absolute top-3 right-3 bg-green-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
              <CheckCircle className="h-3 w-3" />
              Booked
            </div>
          )}
          {service.category && !isBooked && (
            <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-700">
              {service.category}
            </div>
          )}
        </div>

        {/* Service Details */}
        <div className="p-5 flex-1 flex flex-col">
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600" onClick={onClick}>
            {service.name}
          </h3>

          <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-1">
            {service.description}
          </p>

          {/* Service Meta */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              {service.duration && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>{service.duration}</span>
                </div>
              )}
              {service.deliveryMethod && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="capitalize text-xs">{service.deliveryMethod.replace('-', ' ')}</span>
                </div>
              )}
            </div>

            {service.rating && (
              <div className="flex items-center gap-1.5">
                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                <span className="font-medium text-sm">{service.rating}</span>
                {service.reviewCount && (
                  <span className="text-xs text-gray-400">({service.reviewCount} reviews)</span>
                )}
              </div>
            )}
          </div>

          {/* Price */}
          {service.price && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-red-600">R{service.price}</span>
                {service.duration && (
                  <span className="text-sm text-gray-500">/ session</span>
                )}
              </div>
            </div>
          )}

          {/* Features Preview */}
          {service.features && service.features.length > 0 && (
            <div className="mb-4 space-y-1.5">
              {service.features.slice(0, 3).map((feature, index) => (
                <div key={index} className="flex items-start gap-2 text-xs">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600 line-clamp-1">{feature}</span>
                </div>
              ))}
            </div>
          )}

          {/* Service Options Preview */}
          {hasOptions && !isBooked && (
            <div className="mb-4 space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <SlidersHorizontal className="h-3 w-3" />
                <span className="font-medium">{service.options.length} options available</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {service.options.slice(0, 2).map((option, index) => (
                  <span
                    key={index}
                    className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full"
                  >
                    {option.name} {!option.isFree && `+R{option.price}`}
                  </span>
                ))}
                {service.options.length > 2 && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    +{service.options.length - 2}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 mt-auto">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBookClick();
              }}
              disabled={loading}
              className={`w-full py-2.5 px-4 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 ${
                isBooked ? 'ring-2 ring-green-500 ring-inset' : ''
              }`}
              style={{ 
                backgroundColor: isBooked ? '#10b981' : (styling.primaryColor || '#3b82f6')
              }}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {getButtonIcon()}
                  {getButtonText()}
                </>
              )}
            </button>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact?.();
                }}
                className="flex-1 py-2 px-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5 text-sm"
              >
                <MessageCircle className="h-4 w-4" />
                Inquire
              </button>
              <button
                onClick={onClick}
                className="px-3 py-2 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
              >
                Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};